import React, { useEffect, useMemo, useRef, useState } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import Select, { ValueType } from 'react-select';
import ReactDatePicker from 'react-datepicker';
import moment from 'moment';

import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import {
  InputText,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { TextArea } from '../../../../../shared/components/core-ui/text-area/text-area.component';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import { isErrorInfo, ErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { getAllProperties } from '../../../../../PropertyModule/shared/services/data.service';
import { getAllUnitTypes } from '../../../../../shared/services/data/unit-type.data';
import { PropertyUiModel } from '../../../../../shared/ui-models/property.ui-model';
import { getModuleName } from '../../../../shared/services/block-dates.service';
import { bulkUpdateBlockDates } from '../../../../shared/services/data.service';

interface BulkBlockDatesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type BulkBlockDateScope = 'project' | 'selectedUnits';
type BulkBlockDateType = 'isOwnerStay' | 'other' | 'isMaintenance';

type BulkBlockDateTarget = {
  unitTypeId: string;
  unitTypeName: string;
  roomId: string;
  roomName: string;
  propertyName: string;
};

const bulkBlockDateTypeOptions: { value: BulkBlockDateType; label: string }[] = [
  { value: 'isOwnerStay', label: 'Owner stay' },
  { value: 'isMaintenance', label: 'Maintenance' },
  { value: 'other', label: 'Other' }
];

const bulkBlockDateScopeOptions: { value: BulkBlockDateScope; label: string }[] = [
  { value: 'project', label: 'Entire project' },
  { value: 'selectedUnits', label: 'Selected units' }
];

const ownerStayDescription = 'Owners Visit';
const maintenanceDescription = 'blocked for Maintenance';

const getTodayStart = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const BulkBlockDatesModal: React.FC<BulkBlockDatesModalProps> = ({ open, onClose, onSuccess }) => {
  const [projectOptions, setProjectOptions] = useState<{ value: string; label: string }[]>([]);
  const [bulkScope, setBulkScope] = useState<BulkBlockDateScope>('project');
  const [bulkProject, setBulkProject] = useState<{ value: string; label: string } | null>(null);
  const [bulkTargets, setBulkTargets] = useState<BulkBlockDateTarget[]>([]);
  const [selectedBulkRoomIds, setSelectedBulkRoomIds] = useState<string[]>([]);
  const [bulkUnitSearch, setBulkUnitSearch] = useState('');
  const [bulkUnitsLoading, setBulkUnitsLoading] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkFromDate, setBulkFromDate] = useState<Date | undefined>(undefined);
  const [bulkToDate, setBulkToDate] = useState<Date | undefined>(undefined);
  const [bulkDescription, setBulkDescription] = useState('');
  const [bulkBlockDateType, setBulkBlockDateType] = useState<BulkBlockDateType>('other');
  const bulkUnitsRequestIdRef = useRef(0);
  const today = useMemo(() => getTodayStart(), []);
  const bulkDatePickerRenderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled
  }: {
    date: Date;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    prevMonthButtonDisabled: boolean;
    nextMonthButtonDisabled: boolean;
  }) => (
    <div className="flex items-center justify-between px-3 py-2">
      <button
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className="text-[1.6rem] disabled:opacity-40"
      >
        ‹
      </button>
      <span className="text-[1.5rem] font-gilroy-bold">{moment(date).format('MMMM yyyy')}</span>
      <button
        type="button"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        className="text-[1.6rem] disabled:opacity-40"
      >
        ›
      </button>
    </div>
  );

  useEffect(() => {
    if (!open) return;

    getAllProperties().then((response: PropertyUiModel[] | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setProjectOptions(
          response.map((project) => ({ value: project.id || '', label: project.name || '' }))
        );
      }
    });
  }, [open]);

  const filteredBulkTargets = useMemo(() => {
    if (!bulkUnitSearch.trim()) return bulkTargets;

    const query = bulkUnitSearch.toLowerCase();
    return bulkTargets.filter(
      (target) =>
        target.roomName.toLowerCase().includes(query) ||
        target.unitTypeName.toLowerCase().includes(query) ||
        target.propertyName.toLowerCase().includes(query)
    );
  }, [bulkTargets, bulkUnitSearch]);

  const selectedBulkTargets = useMemo(() => {
    if (bulkScope === 'project') return [];
    return bulkTargets.filter((target) => selectedBulkRoomIds.includes(target.roomId));
  }, [bulkScope, bulkTargets, selectedBulkRoomIds]);

  const isBulkDateRangeValid =
    !!bulkFromDate && !!bulkToDate && bulkFromDate >= today && bulkToDate >= bulkFromDate;

  const canSubmitBulkBlockDate =
    !!bulkProject &&
    isBulkDateRangeValid &&
    !bulkSubmitting &&
    (bulkScope === 'project' || (!bulkUnitsLoading && selectedBulkTargets.length > 0)) &&
    (bulkBlockDateType === 'isOwnerStay' ||
      bulkBlockDateType === 'isMaintenance' ||
      !!bulkDescription.trim());

  const bulkTargetLabel =
    bulkScope === 'project'
      ? bulkProject
        ? 'Entire project'
        : 'No project selected'
      : `${selectedBulkTargets.length} target unit(s)`;

  const resetForm = () => {
    bulkUnitsRequestIdRef.current += 1;
    setBulkUnitsLoading(false);
    setBulkScope('project');
    setBulkProject(null);
    setBulkTargets([]);
    setSelectedBulkRoomIds([]);
    setBulkUnitSearch('');
    setBulkFromDate(undefined);
    setBulkToDate(undefined);
    setBulkDescription('');
    setBulkBlockDateType('other');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const loadBulkProjectUnits = (projectId: string, projectLabel: string) => {
    const requestId = bulkUnitsRequestIdRef.current + 1;
    bulkUnitsRequestIdRef.current = requestId;

    setBulkUnitsLoading(true);
    const promise = getAllUnitTypes(getModuleName(), 0, -1, {
      propertyId: projectId,
      isManaged: true
    })
      .then((response) => {
        if (bulkUnitsRequestIdRef.current !== requestId) return;
        if (isErrorInfo(response)) {
          throw response;
        }

        const targets = (response.unitList || []).flatMap((unit) =>
          (unit.units || []).map((room) => ({
            unitTypeId: unit.id || '',
            unitTypeName: unit.name || 'Untitled Unit Type',
            roomId: room.unitId,
            roomName: room.shortName || room.roomNumber || room.unitId,
            propertyName: unit.propertyName || projectLabel
          }))
        );

        setBulkTargets(targets.filter((target) => target.unitTypeId && target.roomId));
      })
      .finally(() => {
        if (bulkUnitsRequestIdRef.current === requestId) {
          setBulkUnitsLoading(false);
        }
      });

    birdToast.promise(promise, {
      loadingMessage: 'Loading project units...',
      successMessage: 'Project units loaded successfully',
      errorFailedMessage: 'Failed to load project units',
      errorPermissionMessage: 'You do not have permission to load project units'
    });
  };

  const handleBulkProjectChange = (value: { value: string; label: string } | null) => {
    bulkUnitsRequestIdRef.current += 1;

    setBulkProject(value);
    setBulkTargets([]);
    setSelectedBulkRoomIds([]);
    setBulkUnitSearch('');

    if (!value?.value) {
      setBulkUnitsLoading(false);
      return;
    }

    if (bulkScope === 'selectedUnits') {
      loadBulkProjectUnits(value.value, value.label);
    } else {
      setBulkUnitsLoading(false);
    }
  };

  const toggleBulkRoom = (roomId: string) => {
    setSelectedBulkRoomIds((current) =>
      current.includes(roomId) ? current.filter((id) => id !== roomId) : [...current, roomId]
    );
  };

  const toggleVisibleBulkRooms = () => {
    const visibleIds = filteredBulkTargets.map((target) => target.roomId);
    const allVisibleSelected = visibleIds.every((id) => selectedBulkRoomIds.includes(id));

    setSelectedBulkRoomIds((current) => {
      if (allVisibleSelected) return current.filter((id) => !visibleIds.includes(id));
      return Array.from(new Set([...current, ...visibleIds]));
    });
  };

  const handleBulkBlockDates = () => {
    if (!canSubmitBulkBlockDate) return;

    setBulkSubmitting(true);
    const description =
      bulkBlockDateType === 'isOwnerStay'
        ? ownerStayDescription
        : bulkBlockDateType === 'isMaintenance'
        ? maintenanceDescription
        : bulkDescription;
    const datePayload = {
      fromDate: moment(bulkFromDate).format('DD/MM/YYYY'),
      toDate: moment(bulkToDate).format('DD/MM/YYYY'),
      description,
      ...(bulkBlockDateType === 'isOwnerStay' && { isOwnerStay: true }),
      ...(bulkBlockDateType === 'isMaintenance' && { isMaintenance: true })
    };

    const payload =
      bulkScope === 'project'
        ? {
            propertyId: bulkProject?.value,
            ...datePayload
          }
        : {
            unitIds: selectedBulkTargets.map((target) => target.roomId),
            ...datePayload
          };
    const targetMessage =
      bulkScope === 'project' ? bulkProject?.label : `${selectedBulkTargets.length} unit(s)`;

    const promise = bulkUpdateBlockDates(payload)
      .then((response) => {
        if (isErrorInfo(response)) throw response;

        resetForm();
        onSuccess();
        onClose();
        return response;
      })
      .finally(() => {
        setBulkSubmitting(false);
      });

    birdToast.promise(promise, {
      loadingMessage: `Blocking dates for ${targetMessage}...`,
      successMessage: `Dates blocked for ${targetMessage}`,
      errorFailedMessage: 'Failed to block dates',
      errorPermissionMessage: 'You do not have permission to block dates'
    });
  };

  return (
    <Modal
      config={{ showModal: open, fullScreen: false, showClose: true, maxWidth: 'lg' }}
      eventHandlers={{ closeModalHandler: handleClose }}
    >
      <div className="w-full p-4 md:p-8 font-gilroy-regular">
        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-[1.9rem] leading-tight font-gilroy-bold text-gray-900">
              Bulk Block Dates
            </div>
            <div className="mt-1 text-[1.2rem] text-gray-500">
              Select a project, then block dates for all units or only selected units.
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[1.2rem] text-gray-600">
            {bulkTargetLabel}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <span className="mb-1 block text-[1.2rem] font-gilroy-medium text-gray-600">
              Apply to
            </span>
            <Select
              options={bulkBlockDateScopeOptions}
              value={bulkBlockDateScopeOptions.find((option) => option.value === bulkScope)}
              onChange={(value: ValueType<{ value: BulkBlockDateScope; label: string }, false>) => {
                const nextScope = value?.value || 'project';
                setBulkScope(nextScope);
                setSelectedBulkRoomIds([]);
                setBulkUnitSearch('');

                if (nextScope === 'selectedUnits' && bulkProject?.value) {
                  loadBulkProjectUnits(bulkProject.value, bulkProject.label);
                } else {
                  bulkUnitsRequestIdRef.current += 1;
                  setBulkUnitsLoading(false);
                  setBulkTargets([]);
                }
              }}
            />
          </div>
          <div>
            <span className="mb-1 block text-[1.2rem] font-gilroy-medium text-gray-600">
              Project
            </span>
            <Select
              options={projectOptions}
              value={bulkProject}
              isClearable
              placeholder="Choose project"
              onChange={(value: ValueType<{ value: string; label: string }, false>) => {
                handleBulkProjectChange(value || null);
              }}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <span className="block text-[1.2rem] font-gilroy-medium text-gray-700">Date range</span>
            <div className="flex gap-2">
              <ReactDatePicker
                selected={bulkFromDate}
                onChange={(date: Date | null) => {
                  setBulkFromDate(date || undefined);
                  setBulkToDate(undefined);
                }}
                selectsStart
                startDate={bulkFromDate}
                endDate={bulkToDate}
                minDate={today}
                placeholderText="From"
                dateFormat="dd/MM/yyyy"
                renderCustomHeader={bulkDatePickerRenderCustomHeader}
                dayClassName={() => '!w-[1.8em] !leading-[1.8em] !text-[1.4rem]'}
                weekDayClassName={() => '!w-[1.8em] !leading-[1.8em] !text-[1.3rem]'}
                className="h-14 w-full rounded-lg border border-gray-200 px-3 text-[1.4rem] outline-none transition-shadow duration-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              />
              <ReactDatePicker
                selected={bulkToDate}
                onChange={(date: Date | null) => setBulkToDate(date || undefined)}
                selectsEnd
                startDate={bulkFromDate}
                endDate={bulkToDate}
                minDate={bulkFromDate || today}
                placeholderText="To"
                dateFormat="dd/MM/yyyy"
                renderCustomHeader={bulkDatePickerRenderCustomHeader}
                dayClassName={() => '!w-[1.8em] !leading-[1.8em] !text-[1.4rem]'}
                weekDayClassName={() => '!w-[1.8em] !leading-[1.8em] !text-[1.3rem]'}
                className="h-14 w-full rounded-lg border border-gray-200 px-3 text-[1.4rem] outline-none transition-shadow duration-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              />
            </div>
          </div>
        </div>

        {bulkProject && bulkScope === 'project' && (
          <div className="mt-4 rounded-lg border border-primary-100 bg-primary-50 px-4 py-3 text-[1.3rem] text-primary-700">
            This will block dates for the entire {bulkProject.label} project.
          </div>
        )}

        {bulkProject && bulkScope === 'selectedUnits' && (
          <div className="mt-4 space-y-3">
            <div>
              <span className="mb-1 block text-[1.2rem] font-gilroy-medium text-gray-600">
                Search units
              </span>
              <InputText
                data={{ placeholder: 'Search by unit or room name...', value: bulkUnitSearch }}
                config={{ type: InputTextType.text }}
                eventHandlers={{ onChangeHandler: (value: string) => setBulkUnitSearch(value) }}
              />
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200">
              <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-[1.5rem] font-gilroy-bold text-gray-900">Units</div>
                  <div className="text-[1.2rem] text-gray-500">
                    {selectedBulkRoomIds.length} selected out of {filteredBulkTargets.length}{' '}
                    visible
                  </div>
                </div>
                <button
                  type="button"
                  disabled={filteredBulkTargets.length === 0}
                  onClick={toggleVisibleBulkRooms}
                  className="rounded-lg border border-primary-500 px-3 py-2 text-[1.2rem] font-gilroy-medium text-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {filteredBulkTargets.length > 0 &&
                  filteredBulkTargets.every((target) => selectedBulkRoomIds.includes(target.roomId))
                    ? 'Clear visible'
                    : 'Select visible'}
                </button>
              </div>

              {bulkUnitsLoading ? (
                <div className="p-8 text-center text-gray-500">Loading units...</div>
              ) : filteredBulkTargets.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No units found for this project</div>
              ) : (
                <div className="max-h-[300px] overflow-y-auto">
                  {filteredBulkTargets.map((target) => {
                    const isSelected = selectedBulkRoomIds.includes(target.roomId);

                    return (
                      <button
                        key={target.roomId}
                        type="button"
                        className={
                          `flex w-full items-center border-b border-gray-200 bg-transparent px-4 py-3 text-left transition-colors duration-150 last:border-b-0 ` +
                          (isSelected
                            ? 'border-l-2 border-l-primary-500 bg-gray-50'
                            : 'hover:bg-gray-50')
                        }
                        onClick={() => toggleBulkRoom(target.roomId)}
                      >
                        <Checkbox
                          color="primary"
                          checked={isSelected}
                          onChange={() => toggleBulkRoom(target.roomId)}
                          onClick={(event) => event.stopPropagation()}
                        />
                        <div className="ml-2 min-w-0">
                          <div
                            className={
                              'truncate text-[1.4rem] leading-snug ' +
                              (isSelected
                                ? 'font-gilroy-bold text-gray-900'
                                : 'font-gilroy-medium text-gray-800')
                            }
                          >
                            {target.roomName}
                          </div>
                          <div className="truncate text-[1.2rem] font-gilroy-regular text-gray-500">
                            {target.unitTypeName} · {target.propertyName}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <span className="mb-1 block text-[1.2rem] font-gilroy-medium text-gray-600">Type</span>
            <Select
              options={bulkBlockDateTypeOptions}
              value={bulkBlockDateTypeOptions.find((option) => option.value === bulkBlockDateType)}
              onChange={(value: ValueType<{ value: BulkBlockDateType; label: string }, false>) => {
                const nextType = value?.value || 'other';
                setBulkBlockDateType(nextType);
                setBulkDescription(
                  nextType === 'isOwnerStay'
                    ? ownerStayDescription
                    : nextType === 'isMaintenance'
                    ? maintenanceDescription
                    : ''
                );
              }}
            />
          </div>
          {bulkBlockDateType === 'other' && (
            <div>
              <span className="mb-1 block text-[1.2rem] font-gilroy-medium text-gray-600">
                Comment
              </span>
              <TextArea
                data={{ value: bulkDescription, placeholder: 'Add your comment' }}
                config={{ maxRows: 4, className: 'w-full' }}
                eventHandlers={{ onChangeHandler: (value: string) => setBulkDescription(value) }}
              />
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col justify-end gap-3 border-t border-gray-200 pt-4 md:flex-row">
          <button
            type="button"
            onClick={handleClose}
            disabled={bulkSubmitting}
            className="rounded-xl border border-gray-200 px-4 py-2 text-[1.4rem] font-semibold text-slate-700 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleBulkBlockDates}
            disabled={!canSubmitBulkBlockDate}
            className="rounded-xl bg-primary-500 px-4 py-2 text-[1.4rem] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {bulkSubmitting
              ? 'Blocking dates...'
              : bulkScope === 'project'
              ? 'Block Project'
              : `Block ${selectedBulkTargets.length} Unit${
                  selectedBulkTargets.length !== 1 ? 's' : ''
                }`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkBlockDatesModal;
